import { useEffect, useMemo, useState } from 'react'
import { AdminLayout, useAdminAccess } from '../../components/AdminLayout'
import { useLanguage } from '../../contexts/LanguageContext'
import { requestApi } from '../../lib/adminApi'

type StatsOverview = {
  total_users: number
  active_users_today: number
  active_users_week: number
  new_users_today: number
  new_users_week: number
  total_revenue_usd: string
  revenue_this_month: string
  total_consumption: number
  pending_orders: number
}

type TrendItem = {
  date: string
  value: number
}

type RevenueItem = {
  date: string
  amount: string
}

const AdminDashboardContent = () => {
  const { t } = useLanguage()
  const { isGranted } = useAdminAccess()
  const [overview, setOverview] = useState<StatsOverview | null>(null)
  const [userTrend, setUserTrend] = useState<TrendItem[]>([])
  const [revenueTrend, setRevenueTrend] = useState<RevenueItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')

  const cards = useMemo(() => {
    if (!overview) return []
    return [
      { label: t('admin.overview.cards.totalUsers'), value: overview.total_users },
      { label: t('admin.overview.cards.activeToday'), value: overview.active_users_today },
      { label: t('admin.overview.cards.newToday'), value: overview.new_users_today },
      { label: t('admin.overview.cards.totalRevenue'), value: `$${overview.total_revenue_usd}` },
      { label: t('admin.overview.cards.revenueMonth'), value: `$${overview.revenue_this_month}` },
      { label: t('admin.overview.cards.totalConsumption'), value: overview.total_consumption },
    ]
  }, [overview, t])

  useEffect(() => {
    if (!isGranted) return
    let isMounted = true
    setLoading(true)
    setError('')

    Promise.all([
      requestApi<{ success: boolean; [key: string]: any }>('/api/v1/admin/stats/overview'),
      requestApi<{ success: boolean; data: TrendItem[] }>('/api/v1/admin/stats/users?days=7'),
      requestApi<{ success: boolean; data: RevenueItem[] }>('/api/v1/admin/stats/revenue?days=7'),
    ])
      .then(([overviewPayload, userPayload, revenuePayload]) => {
        if (!isMounted) return
        setOverview({
          total_users: overviewPayload.total_users,
          active_users_today: overviewPayload.active_users_today,
          active_users_week: overviewPayload.active_users_week,
          new_users_today: overviewPayload.new_users_today,
          new_users_week: overviewPayload.new_users_week,
          total_revenue_usd: overviewPayload.total_revenue_usd,
          revenue_this_month: overviewPayload.revenue_this_month,
          total_consumption: overviewPayload.total_consumption,
          pending_orders: overviewPayload.pending_orders,
        })
        setUserTrend(userPayload.data || [])
        setRevenueTrend(revenuePayload.data || [])
      })
      .catch((err) => {
        if (!isMounted) return
        setError(err instanceof Error ? err.message : t('admin.overview.error'))
      })
      .finally(() => {
        if (!isMounted) return
        setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [t, isGranted])

  return (
    <>
      {loading && (
        <div className="text-sm text-slate-500">{t('admin.overview.loading')}</div>
      )}
      {!loading && error && (
        <div className="text-sm text-rose-500">{error}</div>
      )}
      {!loading && !error && overview && (
        <div className="space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {cards.map((card) => (
              <div key={card.label} className="border border-slate-200 rounded-2xl p-4 bg-slate-50/80">
                <p className="text-xs text-slate-400 font-semibold uppercase tracking-widest">{card.label}</p>
                <p className="text-2xl font-bold text-slate-900 mt-2">{card.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-slate-200 rounded-2xl p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">{t('admin.overview.userTrend')}</h3>
              <div className="space-y-2">
                {userTrend.map((item) => (
                  <div key={item.date} className="flex items-center justify-between text-sm text-slate-600">
                    <span>{item.date}</span>
                    <span className="font-semibold text-slate-900">{item.value}</span>
                  </div>
                ))}
                {userTrend.length === 0 && (
                  <p className="text-xs text-slate-400">{t('admin.overview.empty')}</p>
                )}
              </div>
            </div>

            <div className="border border-slate-200 rounded-2xl p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">{t('admin.overview.revenueTrend')}</h3>
              <div className="space-y-2">
                {revenueTrend.map((item) => (
                  <div key={item.date} className="flex items-center justify-between text-sm text-slate-600">
                    <span>{item.date}</span>
                    <span className="font-semibold text-slate-900">${item.amount}</span>
                  </div>
                ))}
                {revenueTrend.length === 0 && (
                  <p className="text-xs text-slate-400">{t('admin.overview.empty')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export const AdminDashboardScreen = () => {
  const { t } = useLanguage()

  return (
    <AdminLayout title={t('admin.overview.title')} description={t('admin.overview.desc')}>
      <AdminDashboardContent />
    </AdminLayout>
  )
}
